/**
 * B5 · Seller Order Console — mounted at `/business/:id/orders`.
 *
 * The server-side state machine is authoritative. This screen only
 * enumerates the ACTIONS the current state allows and forwards them
 * to `PATCH /orders/{id}/status`. It never posts "paid" / "refunded"
 * — those are set only after signature-verified payment reconciliation.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ChevronLeft, Package, Truck, ClipboardList, RefreshCw,
  CheckCircle2, XCircle, Filter as FilterIcon, ArrowRight,
} from "lucide-react";


const STATE_LABELS = {
  draft: "Draft", pending_payment: "Pending payment", paid: "Paid",
  confirmed: "Confirmed", processing: "Processing", ready: "Ready",
  dispatched: "Dispatched", delivered: "Delivered", completed: "Completed",
  cancelled: "Cancelled", payment_failed: "Payment failed",
  refund_pending: "Refund pending", refunded: "Refunded",
};

// Seller-facing next actions per current state. Buttons never appear
// for illegal transitions — the backend will still refuse them but we
// keep the UI honest.
const SELLER_ACTIONS = {
  paid:        [["confirmed",  "Accept order",    "emerald"]],
  confirmed:   [["processing", "Start processing","emerald"],
                ["cancelled",  "Cancel",          "rose"]],
  processing:  [["ready",      "Mark ready",      "emerald"]],
  ready:       [["dispatched", "Dispatch",        "emerald"],
                ["delivered",  "Mark delivered",  "emerald"]],
  dispatched:  [["delivered",  "Mark delivered",  "emerald"]],
  delivered:   [["completed",  "Mark completed",  "emerald"]],
};

const STATE_FILTERS = [
  "all", "paid", "confirmed", "processing", "ready", "dispatched",
  "delivered", "completed", "cancelled", "payment_failed", "refunded",
];


function StatePill({ state }) {
  const fail = ["cancelled", "payment_failed", "refunded"].includes(state);
  const pend = ["pending_payment", "refund_pending"].includes(state);
  const cls = fail ? "bg-rose-50 text-rose-700 border-rose-200"
              : pend ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-wide ${cls}`}
          data-testid={`seller-order-state-${state}`}>
      {STATE_LABELS[state] || state}
    </span>
  );
}


export default function BusinessOrdersConsole() {
  const { id } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    const q = filter === "all" ? "" : `?status=${encodeURIComponent(filter)}`;
    try {
      const { data } = await api.get(`/business/${id}/orders${q}`);
      setItems(data.orders || []);
      setPicked(new Set());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot load orders");
    } finally { setBusy(false); }
  }, [id, filter]);
  useEffect(() => { load(); }, [load]);

  const toggle = (oid) => setPicked(p => {
    const n = new Set(p);
    n.has(oid) ? n.delete(oid) : n.add(oid);
    return n;
  });
  const selectAllOfStatus = (status) => {
    const ids = items.filter(o => o.status === status).map(o => o.order_id);
    setPicked(new Set(ids));
  };
  const clearSelection = () => setPicked(new Set());

  const bulk = async (to_state) => {
    if (picked.size === 0) { toast.error("Select at least one order"); return; }
    if (!window.confirm(`Set ${picked.size} order(s) to "${to_state}"?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.patch(`/business/${id}/orders/bulk-status`, {
        order_ids: [...picked], to_state, reason: "bulk action",
      });
      if (data.failed > 0)
        toast.warning(`${data.updated} updated · ${data.failed} skipped (illegal transition)`);
      else
        toast.success(`${data.updated} order(s) updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk update failed");
    } finally { setBulkBusy(false); }
  };

  const counts = useMemo(() => {
    const c = {};
    items.forEach(o => { c[o.status] = (c[o.status] || 0) + 1; });
    return c;
  }, [items]);

  // Bulk-action options — only those valid for at least one selected order's
  // current state. We keep it simple: derive from current filter or from
  // the union of selected orders' states.
  const bulkOptions = useMemo(() => {
    const targets = new Set();
    items.filter(o => picked.has(o.order_id)).forEach(o => {
      (SELLER_ACTIONS[o.status] || []).forEach(([t]) => targets.add(t));
    });
    return [...targets];
  }, [picked, items]);

  return (
    <div className="min-h-screen bg-stone-50 pb-24" data-testid="seller-orders-console">
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => nav(`/business/${id}`)} aria-label="Back"><ChevronLeft size={20}/></button>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-black">Business</div>
            <div className="font-black text-stone-900 text-base flex items-center gap-2">
              <ClipboardList size={18}/> Orders console
            </div>
          </div>
          <button onClick={load} aria-label="Refresh"
                  className="rounded-full bg-stone-100 text-stone-700 text-xs font-black p-2"
                  data-testid="seller-orders-refresh">
            <RefreshCw size={14}/>
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-2 flex gap-1 overflow-x-auto">
          {STATE_FILTERS.map(s => (
            <button key={s} onClick={() => setFilter(s)}
                    data-testid={`seller-orders-filter-${s}`}
                    className={`rounded-full text-[10px] font-black px-2.5 py-1 whitespace-nowrap ${
                      filter === s ? "bg-stone-900 text-white"
                                    : "bg-stone-100 text-stone-600"
                    }`}>
              {s === "all" ? "All" : STATE_LABELS[s] || s}
              {counts[s] ? <span className="ml-1 opacity-70">({counts[s]})</span> : null}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 space-y-2">
        {/* Bulk action bar — sticks above the list when anything is picked. */}
        {picked.size > 0 && (
          <div className="rounded-2xl bg-stone-900 text-white p-3 flex items-center gap-2 flex-wrap"
               data-testid="seller-bulk-bar">
            <div className="text-xs font-black">{picked.size} selected</div>
            <button onClick={clearSelection}
                    data-testid="seller-bulk-clear"
                    className="rounded-full bg-stone-800 text-white text-[10px] font-black px-2 py-1">Clear</button>
            <div className="flex-1"/>
            {bulkOptions.map(to => (
              <button key={to} onClick={() => bulk(to)} disabled={bulkBusy}
                      data-testid={`seller-bulk-${to}`}
                      className="rounded-full bg-emerald-500 text-white text-[10px] font-black px-3 py-1 disabled:opacity-50">
                Mark {STATE_LABELS[to] || to}
              </button>
            ))}
            {bulkOptions.length === 0 && (
              <div className="text-[10px] text-stone-400">No common action available for these states</div>
            )}
          </div>
        )}
        {/* Quick-select shortcut for the common case. */}
        {picked.size === 0 && (counts.paid || counts.ready || counts.processing) && (
          <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-2 text-[11px] flex flex-wrap gap-2 items-center"
               data-testid="seller-quick-select">
            <b className="text-emerald-800">Quick select:</b>
            {counts.paid > 0 && (
              <button onClick={() => selectAllOfStatus("paid")}
                      data-testid="seller-quick-select-paid"
                      className="rounded-full bg-white border border-emerald-300 text-emerald-800 font-black px-2 py-0.5">
                All paid ({counts.paid})
              </button>
            )}
            {counts.ready > 0 && (
              <button onClick={() => selectAllOfStatus("ready")}
                      data-testid="seller-quick-select-ready"
                      className="rounded-full bg-white border border-emerald-300 text-emerald-800 font-black px-2 py-0.5">
                All ready ({counts.ready})
              </button>
            )}
            {counts.processing > 0 && (
              <button onClick={() => selectAllOfStatus("processing")}
                      data-testid="seller-quick-select-processing"
                      className="rounded-full bg-white border border-emerald-300 text-emerald-800 font-black px-2 py-0.5">
                All processing ({counts.processing})
              </button>
            )}
          </div>
        )}

        {busy && <div className="text-center py-10 text-stone-400">Loading…</div>}
        {!busy && !items.length && (
          <div className="text-center py-16 text-stone-400" data-testid="seller-orders-empty">
            <Package size={32} className="inline"/>
            <div className="mt-2 text-sm font-black">No orders match this filter</div>
          </div>
        )}
        {items.map(o => (
          <div key={o.order_id}
               data-testid={`seller-order-row-${o.order_id}`}
               className={`rounded-2xl bg-white border p-3 flex items-start gap-3 ${
                 picked.has(o.order_id) ? "border-emerald-500 bg-emerald-50" : "border-stone-200"
               }`}>
            <input type="checkbox" checked={picked.has(o.order_id)}
                   onChange={() => toggle(o.order_id)}
                   data-testid={`seller-order-check-${o.order_id}`}
                   className="mt-2 accent-emerald-600"/>
            <button onClick={() => setSelected(o.order_id)}
                    data-testid={`seller-order-open-${o.order_id}`}
                    className="flex-1 text-left hover:opacity-90">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-stone-100 text-stone-700 grid place-items-center">
                  <Package size={18}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-black text-stone-900 text-sm truncate flex items-center gap-2">
                    <span className="font-mono">{o.order_id.slice(-10)}</span>
                    <StatePill state={o.status}/>
                  </div>
                  <div className="text-[11px] text-stone-500 truncate">
                    {o.currency} {(o.amount_paise/100).toFixed(2)} · {(o.lines || []).length} item(s)
                    · {o.delivery?.method || "pickup"}
                  </div>
                  <div className="text-[11px] text-stone-500">
                    Placed {new Date(o.created_at).toLocaleString()}
                    {o.delivery?.tracking_ref &&
                      <> · Track <span className="font-mono">{o.delivery.tracking_ref}</span></>}
                  </div>
                </div>
                <ArrowRight size={16} className="text-stone-400 mt-2"/>
              </div>
            </button>
          </div>
        ))}
      </div>

      {selected && <OrderDrawer businessId={id} orderId={selected}
                                  onClose={() => setSelected(null)}
                                  onUpdated={() => { setSelected(null); load(); }}/>}
    </div>
  );
}


function OrderDrawer({ businessId, orderId, onClose, onUpdated }) {
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState({ tracking_ref: "", tracking_url: "", eta_iso: "" });
  const load = () => api.get(`/orders/${orderId}`).then(r => {
    setO(r.data);
    setTracking({
      tracking_ref: r.data.delivery?.tracking_ref || "",
      tracking_url: r.data.delivery?.tracking_url || "",
      eta_iso:      r.data.delivery?.eta_iso      || "",
    });
  }).catch(e => toast.error(e?.response?.data?.detail || "Cannot load order"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orderId]);

  const patch = async (to_state, reason = "") => {
    setBusy(true);
    try {
      const body = { to_state, reason };
      // Attach tracking on dispatch — server merges shallowly.
      if (to_state === "dispatched" && (tracking.tracking_ref || tracking.tracking_url || tracking.eta_iso)) {
        body.tracking = { method: o?.delivery?.method || "seller_delivery", ...tracking };
      }
      await api.patch(`/orders/${orderId}/status`, body);
      toast.success(`Order → ${STATE_LABELS[to_state] || to_state}`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cannot update");
    } finally { setBusy(false); }
  };

  const actions = SELLER_ACTIONS[o?.status] || [];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center p-2" onClick={onClose}>
      <div className="bg-white rounded-3xl w-full max-w-lg p-5 space-y-3 max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()} data-testid="seller-order-drawer">
        {!o && <div className="text-center py-6 text-stone-400">Loading…</div>}
        {o && <>
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Order</div>
              <div className="font-mono font-black text-stone-900">{o.order_id}</div>
            </div>
            <StatePill state={o.status}/>
          </div>

          <div className="rounded-xl bg-stone-50 border border-stone-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black">Total</div>
            <div className="text-2xl font-black text-stone-900">{o.currency} {(o.amount_paise/100).toFixed(2)}</div>
            <div className="text-[11px] text-stone-500 mt-1">
              Placed {new Date(o.created_at).toLocaleString()}
            </div>
            {o.payment_txn_id && (
              <div className="text-[11px] text-stone-500 mt-1">
                Payment txn: <span className="font-mono">{o.payment_txn_id}</span>
              </div>
            )}
            {o.ledger_entry_id && (
              <div className="text-[11px] text-stone-500">
                Day-Book entry: <span className="font-mono">{o.ledger_entry_id}</span>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="rounded-xl border border-stone-200 p-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black mb-1">Items</div>
            {(o.lines || []).map((l, i) => (
              <div key={i} className="flex justify-between items-center py-1 border-b border-stone-100 last:border-0 text-sm">
                <div><b className="text-stone-900">{l.name}</b>
                  <span className="text-[11px] text-stone-500"> · qty {l.qty} · {l.kind}</span>
                </div>
                <div className="font-black text-stone-800">{o.currency} {(l.qty * l.unit_price_paise/100).toFixed(2)}</div>
              </div>
            ))}
          </div>

          {/* Delivery */}
          <div className="rounded-xl border border-stone-200 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-black flex items-center gap-1">
              <Truck size={12}/> Delivery
            </div>
            <div className="text-sm text-stone-800">
              <div>Method: <b>{o.delivery?.method || "pickup"}</b></div>
              {o.delivery?.address && <div>{o.delivery.address}{o.delivery.city ? `, ${o.delivery.city}` : ""}</div>}
              {o.delivery?.contact_phone && <div className="font-mono">{o.delivery.contact_phone}</div>}
            </div>
            {/* Tracking fields (edited pre-dispatch) */}
            {["ready", "processing"].includes(o.status) && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                <input value={tracking.tracking_ref}
                       onChange={e => setTracking({...tracking, tracking_ref: e.target.value})}
                       placeholder="Tracking reference"
                       data-testid="seller-input-tracking-ref"
                       className="rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
                <input value={tracking.tracking_url}
                       onChange={e => setTracking({...tracking, tracking_url: e.target.value})}
                       placeholder="Tracking URL (optional)"
                       data-testid="seller-input-tracking-url"
                       className="rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
                <input value={tracking.eta_iso}
                       onChange={e => setTracking({...tracking, eta_iso: e.target.value})}
                       placeholder="ETA (ISO datetime, optional)"
                       data-testid="seller-input-eta"
                       className="col-span-2 rounded-xl border border-stone-200 px-2 py-1.5 text-xs"/>
              </div>
            )}
          </div>

          {/* Actions */}
          {actions.length > 0 && (
            <div className="grid grid-cols-2 gap-2">
              {actions.map(([to, label, color]) => (
                <button key={to} onClick={() => patch(to)}
                        disabled={busy}
                        data-testid={`seller-order-action-${to}`}
                        className={`rounded-full text-xs font-black py-2 disabled:opacity-50 ${
                          color === "rose"
                            ? "bg-rose-50 text-rose-700"
                            : "bg-emerald-600 text-white"
                        }`}>
                  {label}
                </button>
              ))}
            </div>
          )}
          {actions.length === 0 && (
            <div className="text-center text-[11px] text-stone-500">
              No seller actions available in state <b>{STATE_LABELS[o.status]}</b>.
              {o.status === "pending_payment" &&
                <div>Waiting for the customer to complete payment. Server posts a Day-Book entry only after the payment webhook is verified.</div>}
            </div>
          )}

          {/* Audit trail */}
          <details className="rounded-xl border border-stone-200 p-3" data-testid="seller-order-audit">
            <summary className="text-[10px] uppercase tracking-widest text-stone-500 font-black cursor-pointer">
              Audit trail
            </summary>
            <div className="mt-2 space-y-1 text-[11px] text-stone-600">
              {(o.events || []).map(ev => (
                <div key={ev.event_id} className="flex justify-between gap-2">
                  <span className="font-mono">{ev.action}</span>
                  <span>{ev.from_state || "—"} → {ev.to_state || "—"}</span>
                  <span>{new Date(ev.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </details>
        </>}
      </div>
    </div>
  );
}
